import { Box, NumberInput as ChakraNumberInput, Field } from '@chakra-ui/react';
import React from 'react';

interface NumberInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  isRequired?: boolean;
  size?: 'sm' | 'md' | 'lg';
  min?: number;
  max?: number;
  step?: number;
  precision?: number; // Convenience prop - gets converted to formatOptions
  useGrouping?: boolean; // Convenience prop - gets converted to formatOptions
  readOnly?: boolean;
  hideSteppers?: boolean; // Hide increment/decrement buttons
  helperText?: string;
  formatOptions?: Intl.NumberFormatOptions;
}

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      label,
      value,
      onChange,
      onBlur,
      onFocus,
      isRequired = false,
      size = 'md',
      min,
      max,
      step = 1,
      precision,
      useGrouping,
      readOnly = false,
      hideSteppers = false,
      helperText,
      formatOptions,
      ...props
    },
    ref
  ) => {
    const [touched, setTouched] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Combine refs
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const handleChange = (valueString: string) => {
      onChange(valueString);
    };

    const handleBlur = () => {
      setTouched(true);
      if (onBlur) {
        onBlur();
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      // Select all text on focus
      e.target.select();
      if (onFocus) {
        onFocus(e);
      }
    };

    const isInvalidValue = () => {
      // Don't validate empty values - let parent component handle required validation
      if (value === '') {
        return false;
      }

      // Strip currency/formatting characters before parsing (handles $, commas, etc.)
      const cleanValue = value.replace(/[^\d.-]/g, '');
      const numValue = parseFloat(cleanValue);
      if (Number.isNaN(numValue)) return true;
      if (min !== undefined && numValue < min) return true;
      if (max !== undefined && numValue > max) return true;

      return false;
    };

    const getErrorMessage = () => {
      if (value === '') {
        return 'This field is required';
      }
      if (min !== undefined && max !== undefined) return `Value must be between ${min} and ${max}`;
      if (min !== undefined) return `Value must be at least ${min}`;
      if (max !== undefined) return `Value must be at most ${max}`;
      return 'Please enter a valid number';
    };

    // Merge convenience props into formatOptions
    // Explicitly set style: 'decimal' to prevent Intl.NumberFormat from ever
    // using currency formatting (which can happen due to browser locale or state issues)
    const mergedFormatOptions: Intl.NumberFormatOptions = {
      style: 'decimal', // Explicit decimal style prevents currency symbols
      ...formatOptions,
      ...(precision !== undefined && {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }),
      ...(useGrouping !== undefined && { useGrouping }),
    };

    const inputElement = (
      <Box width="100%">
        <ChakraNumberInput.Root
          size={size}
          value={value || ''}
          step={step}
          min={min}
          max={max}
          formatOptions={mergedFormatOptions}
          width="100%"
          invalid={touched && isInvalidValue()}
          onValueChange={({ value }: { value: string }) => handleChange(value)}
        >
          {!hideSteppers && (
            <ChakraNumberInput.Control>
              <ChakraNumberInput.IncrementTrigger />
              <ChakraNumberInput.DecrementTrigger />
            </ChakraNumberInput.Control>
          )}
          <ChakraNumberInput.Input
            ref={inputRef}
            placeholder=""
            readOnly={readOnly}
            onBlur={handleBlur}
            onFocus={handleFocus}
            {...props}
          />
        </ChakraNumberInput.Root>
      </Box>
    );

    // If no label, return just the input
    if (!label) {
      return inputElement;
    }

    return (
      <Field.Root required={isRequired}>
        <Field.Label>
          {label} {isRequired && <Field.RequiredIndicator />}
        </Field.Label>
        {inputElement}
        {helperText && <Field.HelperText>{helperText}</Field.HelperText>}
        {touched && isInvalidValue() && (
          <Field.HelperText color="red.500">{getErrorMessage()}</Field.HelperText>
        )}
      </Field.Root>
    );
  }
);

NumberInput.displayName = 'NumberInput';
